






























































import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";
import { MicroserviceIcon } from "sitewhere-ide-common";

import NewCommandDestinationChooser from "./NewCommandDestinationChooser.vue";
import CoapCommandDestinationCreateDialog from "./coap/CoapCommandDestinationCreateDialog.vue";
import CoapCommandDestinationUpdateDialog from "./coap/CoapCommandDestinationUpdateDialog.vue";
import MqttCommandDestinationCreateDialog from "./mqtt/MqttCommandDestinationCreateDialog.vue";
import MqttCommandDestinationUpdateDialog from "./mqtt/MqttCommandDestinationUpdateDialog.vue";

import {
  ContentSection,
  ContentDeleteIcon,
  DatatableSection,
  DatatableLink,
  ContentLink,
  SectionOverview
} from "sitewhere-ide-components";

import { ICommandDestinationGenericConfiguration } from "sitewhere-configuration-model";

/** Format for displaying data */
interface IDisplayedDestination {
  meta: { id: string; type: string };
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  config: any;
}

@Component({
  components: {
    ContentSection,
    DatatableSection,
    DatatableLink,
    ContentDeleteIcon,
    ContentLink,
    SectionOverview,
    NewCommandDestinationChooser,
    CoapCommandDestinationCreateDialog,
    CoapCommandDestinationUpdateDialog,
    MqttCommandDestinationCreateDialog,
    MqttCommandDestinationUpdateDialog
  }
})
export default class CommandDeliveryTable extends Vue {
  @Prop() readonly tenantId!: string;
  @Prop() readonly destinations!: ICommandDestinationGenericConfiguration[];
  @Ref() readonly chooser!: NewCommandDestinationChooser;
  @Ref() readonly coapCreate!: CoapCommandDestinationCreateDialog;
  @Ref() readonly coapUpdate!: CoapCommandDestinationUpdateDialog;
  @Ref() readonly mqttCreate!: MqttCommandDestinationCreateDialog;
  @Ref() readonly mqttUpdate!: MqttCommandDestinationUpdateDialog;

  headers: { text: string; value: string }[] = [
    { text: "Id", value: "id" },
    { text: "Type", value: "type" },
    { text: "", value: "delete" }
  ];

  /** Get page icon */
  get icon(): MicroserviceIcon {
    return MicroserviceIcon.CommandDelivery;
  }

  /** Get list of ids already in use */
  findIdsInUse(exclude?: string): string[] {
    const ids: string[] = [];
    if (this.destinations) {
      this.destinations.forEach(dest => {
        if (dest.id != exclude) {
          ids.push(dest.id);
        }
      });
    }
    return ids;
  }

  /** Get command destination index based on id */
  getCommandDestinationIndex(id: string): number | null {
    let match: number | null = null;
    if (this.destinations) {
      this.destinations.forEach((dest, index) => {
        if (dest.id === id) {
          match = index;
        }
      });
    }
    return match;
  }

  /** Get a command destination by id */
  getCommandDestinationById(
    id: string
  ): ICommandDestinationGenericConfiguration | null {
    const index: number | null = this.getCommandDestinationIndex(id);
    if (this.destinations && index != null) {
      return this.destinations[index];
    }
    return null;
  }

  /** Get command destinations as a sorted array */
  get displayCommandDestinations(): IDisplayedDestination[] {
    const all: IDisplayedDestination[] = [];
    if (this.destinations) {
      this.destinations.forEach(dest => {
        const meta: { id: string; type: string } = {
          id: dest.id,
          type: dest.type
        };
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const config: any = dest.configuration;
        all.push({ meta: meta, config: config });
      });
    }
    all.sort(function(a, b) {
      return a.meta.id.localeCompare(b.meta.id);
    });
    return all;
  }

  /** Add new command destination */
  onAddCommandDestination() {
    this.chooser.openChooser();
  }

  /** Called to create a new command destination based on type */
  onCommandDestinationCreate(id: string): void {
    const idsInUse: string[] = this.findIdsInUse();
    if (id == "coap") {
      this.coapCreate.openDialog(idsInUse);
    } else if (id == "mqtt") {
      this.mqttCreate.openDialog(idsInUse);
    }
  }

  /** Called when command destination is added */
  onCommandDestinationAdded(
    config: ICommandDestinationGenericConfiguration
  ): void {
    if (this.destinations) {
      this.destinations.push(config);
    }
    this.$emit("create", config);
  }

  /** Open command destination by id */
  onOpenCommandDestination(id: string) {
    const config: ICommandDestinationGenericConfiguration | null = this.getCommandDestinationById(
      id
    );
    const idsInUse: string[] = this.findIdsInUse(id);
    if (config) {
      if (config.type === "coap") {
        this.coapUpdate.openDialog(config, idsInUse);
      } else if (config.type === "mqtt") {
        this.mqttUpdate.openDialog(config, idsInUse);
      }
    }
  }

  /** Called when command destination is updated */
  onCommandDestinationUpdated(
    originalId: string,
    config: ICommandDestinationGenericConfiguration
  ): void {
    const index: number | null = this.getCommandDestinationIndex(originalId);
    if (this.destinations && index != null) {
      Vue.set(this.destinations, index, config);
    }
    this.$emit("update", originalId, config);
  }

  /** Delete command destination by id */
  onDeleteCommandDestination(id: string) {
    const index: number | null = this.getCommandDestinationIndex(id);
    if (this.destinations && index != null) {
      this.destinations.splice(index, 1);
    }
    this.$emit("delete", id);
  }
}
